// Import React library for creating components
import React from 'react';
// Import Flag icon from Lucide React icon library
import { Flag } from 'lucide-react';

// Define the shape of props this priority badge component expects
interface TaskPriorityBadgeProps {
  // priority: Optional task priority level
  // Can be one of four specific values: Low, Medium, High, or Critical
  // The ? makes this optional - component handles cases where no priority is set
  priority?: 'Low' | 'Medium' | 'High' | 'Critical';
}

// TaskPriorityBadge displays a colored badge indicating the priority level of a task
// Each priority level gets a different color to help users quickly identify urgent work
function TaskPriorityBadge({ priority }: TaskPriorityBadgeProps) {
  // Don't render anything if no priority is set
  // This is a common React pattern for conditional rendering
  if (!priority) return null;

  // Function to determine the appropriate colors for each priority level
  // Returns Tailwind CSS classes for background and text colors
  const getPriorityColor = () => {
    switch (priority) {
      case 'Critical':
        return 'bg-red-100 text-red-700';       // Red: Most urgent, requires immediate attention
      case 'High':
        return 'bg-orange-100 text-orange-700'; // Orange: High priority, should be done soon
      case 'Medium':
        return 'bg-yellow-100 text-yellow-700'; // Yellow: Medium priority, normal workflow
      case 'Low':
        return 'bg-green-100 text-green-700';   // Green: Low priority, can wait if needed
      default:
        return 'bg-gray-100 text-gray-700';     // Gray: Fallback for unexpected values
    }
  };

  // Render the priority badge with appropriate colors
  return (
    // Small rounded badge with flag icon and priority text
    // inline-flex: displays inline but with flex capabilities for icon alignment
    // items-center: vertically centers the icon and text
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor()}`}>
      {/* Small flag icon to visually represent priority */}
      <Flag className="w-3 h-3 mr-1" />
      {/* Display the priority level text (Low, Medium, High, Critical) */}
      {priority}
    </span>
  );
}

// Export the component as the default export so other files can import it
// Usage: import TaskPriorityBadge from './TaskPriorityBadge'
export default TaskPriorityBadge;